/* eslint-disable react-hooks/set-state-in-effect */
import './Tabs.scss';

import { AnimatePresence, motion } from 'motion/react';
import React, {
  Fragment,
  PropsWithChildren,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { BACKWARD, FORWARD } from '../../constants/navigationConstants';
import { cn } from '../../helpers/classBuilders';
import { useClassHelper } from '../../hooks';
import { useResizeWindow } from '../../hooks/useResizeWindow';
import { Stack } from '../Stack';
import Dropdown from './Dropdown';
import ScrollButtons from './ScrollButtons';
import TabEmptyItem from './TabEmptyItem';
import TabItem from './TabItem';
import { TabMenuActions } from './TabMenuActions';

const variants = {
  primary: 'primary',
  secondary: 'secondary',
  unstyled: 'unstyled',
  'unstyled-no-border': 'unstyled-no-border',
} as const;

export interface TabMenuFunction {
  onClick: (activeTab: string, setActiveTab: (id: string) => void) => void;
  disabled?: (activeTab: string) => boolean;
  label: string;
}

interface TabsProps extends PropsWithChildren {
  defaultActiveTab?: string | null;
  showNavigation?: boolean;
  overlayLoadingComponent?: React.ReactNode;
  overlayEmptyMessage?: string;
  grouping?: boolean;
  hideTabsOnPrint?: boolean;
  customControlComponent?: React.JSXElementConstructor<any>;
  isLoading?: boolean;
  onChange?: (activeTab: string | null) => void;
  variant?: (typeof variants)[keyof typeof variants];
  nextTab?: string;
  // If you want to control tab state from outside (controlled component) you should use this props
  controlledActiveTab?: string | number;
  controlledOnChange?: (activeTab: string | null) => void;
  tabMenuActions?: TabMenuFunction[];
  classNames?: {
    content?: string;
    list?: string;
  };
}

const findIndexHandler = (activeTab: string | null) => {
  return ({ props: { eventKey } }: any) => {
    return eventKey === activeTab;
  };
};

interface CustomControlStackProps extends PropsWithChildren {
  customControlComponent: React.JSXElementConstructor<any> | null;
  activeTab: string | null;
  classBuilder: (...args: any[]) => string;
}

const CustomControlStack = ({
  customControlComponent: ControlComponent,
  activeTab,
  classBuilder: c,
  children,
}: CustomControlStackProps) => {
  if (!ControlComponent) {
    return <>{children}</>;
  }

  return (
    <Stack direction="row" gap={8} tag="section" justify="between">
      <Stack.Item flex="custom" grow={0} shrink={1} basis="auto" tag="section">
        {children}
      </Stack.Item>

      <Stack.Item flex="custom" grow={1} shrink={1} basis="auto" tag="section">
        <div className={c('__custom_control')}>
          <ControlComponent activeTab={activeTab} />
        </div>
      </Stack.Item>
    </Stack>
  );
};

export const Tabs: React.FC<TabsProps> & {
  Item: typeof TabItem;
  EmptyItem: typeof TabEmptyItem;
} = ({
  children,
  defaultActiveTab = null,
  showNavigation = false,
  customControlComponent: CustomControlComponent = null,
  grouping = false,
  isLoading = false,
  hideTabsOnPrint = false,
  onChange = () => {},
  overlayLoadingComponent = null,
  overlayEmptyMessage = '',
  variant = variants.primary,
  nextTab = null,
  controlledActiveTab,
  controlledOnChange,
  tabMenuActions = [],
  classNames = {
    content: '',
    list: '',
  },
}) => {
  const parsedControlledActiveTab =
    typeof controlledActiveTab === 'number'
      ? `${controlledActiveTab}`
      : controlledActiveTab;
  const componentName = 'c-Tabs';
  const c = useClassHelper(componentName);

  // TODO - This can be improved by adding just one useState object or useReducer
  const [activeTab, setActiveTab] = useState<string | null>(defaultActiveTab);

  const [navCoordinates, setNavCoordinates] = useState<{ width: number }>({
    width: 0,
  });

  const [toggleMenuCoordinates, setToggleMenuCoordinates] = useState<{
    width: number;
  }>({
    width: 0,
  });
  const [tabsHeight, setTabsHeight] = useState<number>(0);
  const [isDisabled, setIsDisabled] = useState<{
    forward: boolean;
    backward: boolean;
  }>({
    forward: true,
    backward: false,
  });
  const tabsNavItemRefs = useRef<{ [key: string]: HTMLElement | null }>({});

  const tabsRef = useRef<HTMLUListElement | null>(null);
  const tabsToggleMenuRef = useRef<HTMLDivElement | null>(null);
  const scrollNavRef = useRef<HTMLLIElement | null>(null);
  const [tabsOverflow, setTabsOverflow] = useState<boolean>(false);
  const { handlerResize: triggerOverflowHandler } = useResizeWindow(
    tabsRef,
    setTabsOverflow,
  );

  const [tabItems, emptyState] = useMemo(() => {
    const items: any[] = React.Children.toArray(children).filter(
      child => React.isValidElement(child) && child.type === TabItem,
    );

    const emptyStatemItems = React.Children.toArray(children).filter(
      child => React.isValidElement(child) && child.type === TabEmptyItem,
    );

    if (items.length < 1) {
      return [emptyStatemItems, true];
    }

    if (isLoading) {
      return [[...emptyStatemItems, ...items], true];
    }

    return [items, false];
  }, [children]);

  // Re-measure when tabsOverflow changes: the scroll arrows and the "…"
  useLayoutEffect(() => {
    const scrollNavElement = scrollNavRef.current;
    const tabsElement = tabsRef.current;
    const tabsToggleMenuElement = tabsToggleMenuRef.current;

    const newNavWidth = scrollNavElement?.offsetWidth || 0;
    const newToggleMenuWidth = tabsToggleMenuElement?.offsetWidth || 0;

    setTabsHeight(tabsElement?.offsetHeight || 0);
    setNavCoordinates({ width: newNavWidth });
    setToggleMenuCoordinates({ width: newToggleMenuWidth });
    triggerOverflowHandler();
  }, [children, tabsOverflow]);

  /**
   * This effect will run only when nextTab changes its value to truthy
   */
  useEffect(() => {
    if (nextTab) {
      setActiveTab(nextTab);
    }
  }, [nextTab]);

  /**
   * useEffect hook that attaches a 'wheel' event listener to the tabs navigation element,
   * enabling horizontal scrolling with the mouse wheel when the user hovers over the tabs bar.
   * The effect is re-executed whenever the referenced DOM node changes.
   */

  useEffect(() => {
    const tabsNavElement = tabsRef.current;
    if (!tabsNavElement) return;

    const handleWheelEvent = (event: WheelEvent) => {
      // If the dropdown is open and the mouse is over the dropdown, do not trigger horizontal scroll
      const dropdownElement = tabsToggleMenuRef.current;
      if (
        dropdownElement &&
        event.target instanceof Node &&
        dropdownElement.contains(event.target)
      ) {
        // Allow vertical scrolling inside the dropdown
        return;
      }

      if (event.deltaY !== 0) {
        event.preventDefault();
        tabsNavElement.scrollLeft += event.deltaY;
      }
    };

    tabsNavElement.addEventListener('wheel', handleWheelEvent, {
      passive: false,
    });

    return () => {
      tabsNavElement.removeEventListener('wheel', handleWheelEvent);
    };
    // eslint-disable-next-line react-hooks/refs
  }, [tabsRef.current, tabsToggleMenuRef.current]);

  /**
   * Description:
   * This useEffect is used to set the first tab as active
   * if the user doesn't provide a defaultActiveTab every time
   * the children change
   *
   * --- This useEffect will be ignored if the component is controlled
   * by the props controlledActiveTab ---
   */
  useEffect(() => {
    if (parsedControlledActiveTab) {
      return;
    }

    const index = tabItems.findIndex(findIndexHandler(activeTab));
    const tabExists = index > -1;

    if (defaultActiveTab && tabExists) {
      return;
    }

    if (activeTab === null && tabItems.length > 0) {
      setActiveTab(tabItems[0].props.eventKey);
      return;
    }

    if (activeTab && tabItems.length > 0) {
      if (!tabExists) {
        setActiveTab(tabItems[0].props.eventKey);
      }
    }
  }, [children]);

  /**
   * Description:
   * This effect is used to manage the disabled state of the scroll buttons
   * and to call the onChange function when the activeTab changes
   */
  useEffect(() => {
    const index = tabItems.findIndex(
      findIndexHandler(parsedControlledActiveTab ?? activeTab),
    );

    if (index === tabItems.length - 1) {
      setIsDisabled({ forward: true, backward: false });
      onChange(activeTab);
      return;
    }

    if (index === 0) {
      setIsDisabled({ forward: false, backward: true });
      onChange(activeTab);
      return;
    }

    onChange(activeTab);
    setIsDisabled({ forward: false, backward: false });
  }, [activeTab, parsedControlledActiveTab]);

  useEffect(() => {
    if (
      defaultActiveTab &&
      !parsedControlledActiveTab &&
      defaultActiveTab !== activeTab
    ) {
      setActiveTab(defaultActiveTab);
    }
  }, [defaultActiveTab]);

  /**
   * This useEffect is used to handle the movement of the tabs
   * once the user select a tab that is not visible in the scroll navigation
   */
  useLayoutEffect(() => {
    const element =
      tabsNavItemRefs.current[(parsedControlledActiveTab ?? activeTab) || ''];

    if (element) {
      const bounding = element.getBoundingClientRect();
      const parentBounding = element?.parentElement?.getBoundingClientRect();
      if (!parentBounding) return;

      const overflowRightLimit =
        parentBounding?.right - toggleMenuCoordinates.width;
      const overflowLeftLimit = parentBounding?.left + navCoordinates.width;
      const parentNode = element.parentNode as HTMLElement;

      if (bounding.left < overflowLeftLimit) {
        const newPosition =
          parentNode!.scrollLeft - (overflowLeftLimit - bounding.left);

        // this condition is used because the smooth behavior is not working properly when the scroll is at the end
        const behavior = newPosition > 1 ? 'smooth' : 'auto';

        parentNode!.scrollTo({ left: newPosition, behavior });
        return;
      }

      // same idea as above but for the right side
      // taking the right position of the element and compare it with the start position of the toggle menu
      if (bounding.right > overflowRightLimit) {
        const newPosition =
          parentNode!.scrollLeft + bounding.right - overflowRightLimit;

        // If the navigation is present the scrollWidth should be the scrollWidth - the width of the toggle menu
        const scrollWidth =
          parentNode!.scrollWidth -
          (showNavigation ? toggleMenuCoordinates.width : 0);

        const behavior =
          element.offsetLeft + element.offsetWidth < scrollWidth
            ? 'smooth'
            : 'auto';

        parentNode!.scrollTo({ left: newPosition, behavior });
        return;
      }
    }
  }, [
    activeTab,
    parsedControlledActiveTab,
    navCoordinates.width,
    toggleMenuCoordinates.width,
  ]);

  const onClick = useCallback(
    (key: string, onClickProps?: (eventKey: string) => void) => {
      if (onClickProps) {
        onClickProps(key);
      }

      setActiveTab(key);
    },
    [],
  );

  const moveTo = useCallback(
    (action: string) => {
      const index = tabItems.findIndex(
        findIndexHandler(parsedControlledActiveTab ?? activeTab),
      );

      switch (action) {
        case FORWARD: {
          const nextTab = (tabItems[index + 1] as any)?.props?.eventKey;

          if (parsedControlledActiveTab) {
            controlledOnChange && controlledOnChange(nextTab);
            return;
          }

          if (index < tabItems.length - 1) {
            setActiveTab(nextTab);
          }

          return;
        }
        case BACKWARD: {
          const prevTab = (tabItems[index - 1] as any)?.props?.eventKey;

          if (parsedControlledActiveTab) {
            controlledOnChange && controlledOnChange(prevTab);
            return;
          }

          if (index > 0) {
            setActiveTab(prevTab);
          }
          return;
        }

        default:
          return;
      }
    },
    [activeTab, tabItems, parsedControlledActiveTab],
  );

  const getStatusClass = useCallback(
    (baseName: string, eventKey: string) => {
      if (eventKey !== activeTab) {
        return '';
      }

      return baseName + '--active';
    },
    [activeTab],
  );

  const renderContent = () => {
    if (parsedControlledActiveTab) {
      return tabItems.map(
        ({ props: { eventKey, children, onlyRenderOnActive } }: any) => {
          const isActive =
            onlyRenderOnActive && parsedControlledActiveTab === eventKey;

          if (!isActive) {
            return null;
          }

          return (
            <motion.div
              key={eventKey}
              className={`${eventKey} ${c('__content', '__content--active', {
                '__no-padding': hideTabsOnPrint,
              })} ${classNames.content}`}
              role="tabpanel"
              initial={{ opacity: 0, y: 0 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 0 }}
              transition={{ duration: 0.1 }}
            >
              {children}
            </motion.div>
          );
        },
      );
    }

    return tabItems.map(
      ({ props: { eventKey, children, onlyRenderOnActive } }: any) => {
        // Ensure only the active tab's content is rendered
        if (
          !parsedControlledActiveTab &&
          onlyRenderOnActive &&
          eventKey !== activeTab
        ) {
          return null;
        }

        if (eventKey === activeTab) {
          // Render only if active
          return (
            <motion.div
              key={eventKey}
              className={`${eventKey} ${c(
                '__content',
                getStatusClass('__content', eventKey),
              )} ${classNames.content}`}
              role="tabpanel"
              initial={{ opacity: 0, y: 0 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 0 }}
              transition={{ duration: 0.1 }}
            >
              {children}
            </motion.div>
          );
        }

        return null; // Return null for inactive tabs when not using onlyRenderOnActive
      },
    );
  };

  const renderTabItem = () => {
    if (parsedControlledActiveTab) {
      return (
        <Fragment>
          {tabItems.map(({ props }: any) => {
            const isActive =
              props.onlyRenderOnActive &&
              parsedControlledActiveTab === props.eventKey;

            return (
              <TabItem
                {...props}
                key={`tablist-${props.eventKey}`}
                navigation={showNavigation}
                classBuilder={c}
                className={cn(
                  c('__tabs_item', isActive ? '__tabs_item--active' : ''),
                  'bg-gray-100 hover:bg-gray-200',
                )}
                forwardedRef={(e: HTMLElement | null) =>
                  (tabsNavItemRefs.current[props.eventKey] = e)
                }
                onClick={controlledOnChange}
                isActive={isActive}
              />
            );
          })}
        </Fragment>
      );
    }

    return (
      <Fragment>
        {tabItems.map(({ props }: any) => {
          const isActive = props.eventKey === activeTab;

          return (
            <TabItem
              {...props}
              key={`tablist-${props.eventKey}`}
              navigation={showNavigation}
              classBuilder={c}
              className={cn(
                c('__tabs_item', getStatusClass('__tabs_item', props.eventKey)),
                'bg-gray-100 hover:bg-gray-200',
              )}
              forwardedRef={(e: HTMLElement | null) => {
                return (tabsNavItemRefs.current[props.eventKey] = e);
              }}
              onClick={eventKey => {
                if (isActive) {
                  return;
                }

                onClick(eventKey, props.onClick);
              }}
              isActive={isActive}
            />
          );
        })}
      </Fragment>
    );
  };

  return (
    <div
      className={`${componentName} ${c('--' + variant)}`}
      data-empty-state={emptyState}
      data-empty-message={overlayEmptyMessage}
      data-is-loading={isLoading}
    >
      {tabMenuActions?.length > 0 && (
        <TabMenuActions
          setActiveTab={setActiveTab}
          actions={tabMenuActions}
          activeTab={activeTab || ''}
        />
      )}
      <CustomControlStack
        customControlComponent={CustomControlComponent}
        activeTab={activeTab}
        classBuilder={c}
      >
        <ul
          role="tablist"
          className={cn(
            c('__tabs_nav', classNames.list || '', {
              hideOnPrint: hideTabsOnPrint,
            }),
            {
              'border-b border-gray-200': variant === variants.unstyled,
            },
          )}
          ref={tabsRef}
          style={{ paddingRight: toggleMenuCoordinates.width }}
        >
          {tabsOverflow && (
            <ScrollButtons
              forwardedRef={scrollNavRef as React.RefObject<HTMLLIElement>}
              moveTo={moveTo}
              disabledForward={isDisabled.forward}
              disabledBackward={isDisabled.backward}
              classBuilder={c}
              className={c('__tabs_item', '__scroll_nav')}
              show={showNavigation}
            />
          )}
          {renderTabItem()}
          {tabsOverflow && (
            <Dropdown
              classBuilder={c}
              tabsHeight={tabsHeight}
              forwardedRef={tabsToggleMenuRef}
              show={showNavigation}
              grouping={grouping}
            >
              {tabItems.map(({ props }: any) => {
                return (
                  <Dropdown.Item
                    {...props}
                    key={`dropdown-item-${props.eventKey}`}
                    eventKey={props.eventKey}
                    onClick={
                      parsedControlledActiveTab ? controlledOnChange : onClick
                    }
                  >
                    {props.title}
                  </Dropdown.Item>
                );
              })}
            </Dropdown>
          )}
        </ul>
      </CustomControlStack>

      <AnimatePresence mode="wait" key={parsedControlledActiveTab ?? activeTab}>
        {renderContent()}
      </AnimatePresence>

      {overlayLoadingComponent && (
        <div className={c('__loading')}>{overlayLoadingComponent}</div>
      )}
    </div>
  );
};

Tabs.Item = TabItem;
Tabs.EmptyItem = TabEmptyItem;
